"use client";

import { useEffect, useState } from "react";
import { HEADER_BYTES, readImageSize } from "@/lib/image/info";
import type { Size } from "@/lib/image/size";
import { measureImage } from "@/workers/image-client";

const cache = new WeakMap<File, Size>();

async function sizeOf(file: File): Promise<Size | null> {
  const known = cache.get(file);
  if (known) return known;
  const head = new Uint8Array(await file.slice(0, HEADER_BYTES).arrayBuffer());
  const size =
    readImageSize(head) ?? (await measureImage(file).catch(() => null));
  if (size) cache.set(file, size);
  return size;
}

/** Pixel size of each image (read from the file header, or by decoding it in
 *  the worker when the header is unusual). Missing until known. */
export function useImageSizes(files: File[]): Map<File, Size> {
  const [sizes, setSizes] = useState(() => new Map<File, Size>());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const file of files) {
        const size = await sizeOf(file);
        if (cancelled) return;
        if (size)
          setSizes((old) =>
            old.get(file) ? old : new Map(old).set(file, size),
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [files]);
  return sizes;
}

export { sizeOf as imageSizeOf };
